import React, { useRef, useEffect, useMemo } from "react";
import { useThree, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import { useStore } from "../store";
import { POS, HUDSON_LOOK } from "./layout";

const CFG = {
  walk: { style: "strafe", y: 2.6, speed: 26, start: [-8, 2.6, 130], clamp: [180, 190] },
  drive: { style: "steer", y: 3.2, speed: 60, start: [-8, 3.2, 150], clamp: [1200, 900] },
  hudson: { style: "steer", y: 8, speed: 55, start: [-560, 8, 900], clamp: [1000, 1000] },
  bridge: { style: "fly", y: 52, speed: 65, start: [430, 52, 150], clamp: [1400, 1400] },
  helicopter: { style: "heli", y: 300, speed: 55, start: [-60, 300, 720], clamp: [1600, 1600] },
  interior: { style: "interior", speed: 18 },
};

// Soft no-fly bubbles (~100ft buffer) around the tall towers for the helicopter tour.
const HELI_OBSTACLES = [
  { x: -46, z: -46, r: 62 }, // North Tower / 1WTC 2001 footprint
  { x: 24, z: 40, r: 62 }, // South Tower
  { x: -124, z: -112, r: 62 }, // One World Trade Center
];
const HELI_FLOOR = 95; // ~300 ft minimum altitude

// Guided-transit framing targets: when the pilot is idle (no drag / no input) in
// these modes, the camera eases to keep the memorial site centred in frame.
const TRANSIT_LOOK = {
  helicopter: [-46, 70, -46],
  hudson: HUDSON_LOOK, // [-46, 170, -46]
  bridge: [-60, 60, -80],
};

function PilotController({ mode, floor }) {
  const { camera, gl } = useThree();
  const cfg = CFG[mode];
  const yaw = useRef(Math.PI);
  const pitch = useRef(mode === "helicopter" ? -0.1 : -0.05);
  const keys = useRef({});
  const dragging = useRef(false);
  const pinching = useRef(false);
  const pinchStart = useRef(null);
  const lastNonce = useRef(useStore.getState().camResetNonce);
  const last = useRef({ x: 0, y: 0 });
  const floorPos = floor === "f81" || floor === "roof" ? POS.southPool : POS.northPool;
  const floorY = floor === "f81" ? 303 : floor === "roof" ? 422 : 403;
  // Adaptive zoom (FOV) limits — never moves the camera, so no wall clipping.
  const zoom = mode === "interior" ? { min: 32, max: 68 } : { min: 22, max: 82 };

  useEffect(() => {
    if (cfg.style === "interior") {
      camera.position.set(floorPos[0], floorY, floorPos[2] + 18);
      yaw.current = Math.PI;
    } else {
      camera.position.set(...cfg.start);
      yaw.current = Math.atan2(-camera.position.x, -camera.position.z);
    }
    const dom = gl.domElement;
    const down = (e) => {
      dragging.current = true;
      const p = e.touches ? e.touches[0] : e;
      last.current = { x: p.clientX, y: p.clientY };
    };
    const move = (e) => {
      if (pinching.current) return;
      if (!dragging.current) return;
      const p = e.touches ? e.touches[0] : e;
      yaw.current -= (p.clientX - last.current.x) * 0.004;
      pitch.current -= (p.clientY - last.current.y) * 0.004;
      pitch.current = Math.max(-1.2, Math.min(0.9, pitch.current));
      last.current = { x: p.clientX, y: p.clientY };
    };
    const up = () => (dragging.current = false);
    const kd = (e) => (keys.current[e.code] = true);
    const ku = (e) => (keys.current[e.code] = false);
    // ---- manual zoom (desktop mouse-wheel) ----
    const onWheel = (e) => {
      e.preventDefault();
      const s = useStore.getState();
      const next = THREE.MathUtils.clamp(s.fov + (e.deltaY > 0 ? 3 : -3), zoom.min, zoom.max);
      s.setFov(next);
    };
    // ---- manual zoom (mobile pinch-to-zoom) ----
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const ts = (e) => {
      if (e.touches.length === 2) {
        pinching.current = true;
        dragging.current = false;
        pinchStart.current = { d: dist(e.touches), fov: useStore.getState().fov };
      }
    };
    const tm = (e) => {
      if (pinching.current && e.touches.length === 2 && pinchStart.current) {
        e.preventDefault();
        const ratio = dist(e.touches) / (pinchStart.current.d || 1);
        const next = THREE.MathUtils.clamp(pinchStart.current.fov / ratio, zoom.min, zoom.max);
        useStore.getState().setFov(next);
      }
    };
    const te = (e) => {
      if (e.touches.length < 2) {
        pinching.current = false;
        pinchStart.current = null;
      }
    };
    dom.addEventListener("pointerdown", down);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    dom.addEventListener("wheel", onWheel, { passive: false });
    dom.addEventListener("touchstart", ts, { passive: false });
    dom.addEventListener("touchmove", tm, { passive: false });
    dom.addEventListener("touchend", te);
    return () => {
      dom.removeEventListener("pointerdown", down);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("keydown", kd);
      window.removeEventListener("keyup", ku);
      dom.removeEventListener("wheel", onWheel);
      dom.removeEventListener("touchstart", ts);
      dom.removeEventListener("touchmove", tm);
      dom.removeEventListener("touchend", te);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, floor]);

  useFrame((_, delta) => {
    const store = useStore.getState();
    // Apply manual zoom (FOV) smoothly.
    const targetFov = THREE.MathUtils.clamp(store.fov, zoom.min, zoom.max);
    if (Math.abs(camera.fov - targetFov) > 0.01) {
      camera.fov = THREE.MathUtils.lerp(camera.fov, targetFov, 0.25);
      camera.updateProjectionMatrix();
    }
    // Handle "Reset Camera": snap back to this mode's default vantage.
    if (store.camResetNonce !== lastNonce.current) {
      lastNonce.current = store.camResetNonce;
      if (cfg.style === "interior") {
        camera.position.set(floorPos[0], floorY + 1.6, floorPos[2] + 18);
        yaw.current = Math.PI;
        pitch.current = -0.05;
      } else {
        camera.position.set(...cfg.start);
        yaw.current = Math.atan2(-camera.position.x, -camera.position.z);
        pitch.current = mode === "helicopter" ? -0.1 : -0.05;
      }
    }
    const k = keys.current;
    const dp = useStore.getState().dpad;
    const ix = dp.x + (k.KeyD || k.ArrowRight ? 1 : 0) - (k.KeyA || k.ArrowLeft ? 1 : 0);
    // Y-axis: Up (D-pad up / W / ArrowUp) => forward/up, Down => backward/down (all modes).
    const iy = -(dp.y + (k.KeyW || k.ArrowUp ? 1 : 0) - (k.KeyS || k.ArrowDown ? 1 : 0));
    const spd = cfg.speed * delta;
    const c = camera.position;

    if (cfg.style === "steer" || cfg.style === "heli") {
      yaw.current -= ix * delta * 1.1; // steer
    }
    const fwd = new THREE.Vector3(Math.sin(yaw.current), 0, Math.cos(yaw.current));
    const right = new THREE.Vector3(fwd.z, 0, -fwd.x);

    if (cfg.style === "heli") {
      // Input-only motion: D-pad forward/back (iy) + steer yaw (ix). No auto-drift — the
      // camera holds perfectly still when there is no input, and never banks (horizon locked).
      c.addScaledVector(fwd, iy * spd);
      c.y = THREE.MathUtils.clamp(c.y, HELI_FLOOR, 900);
      // Soft no-fly bubble: slide the camera out to the safe radius around each tower.
      for (const o of HELI_OBSTACLES) {
        const dx = c.x - o.x;
        const dz = c.z - o.z;
        const dist = Math.hypot(dx, dz);
        if (dist < o.r && dist > 0.001) {
          const k = o.r / dist;
          c.x = o.x + dx * k;
          c.z = o.z + dz * k;
        }
      }
    } else if (cfg.style === "steer") {
      c.addScaledVector(fwd, iy * spd);
      c.y = cfg.y;
    } else if (cfg.style === "fly") {
      // Bridge Tour: ride ALONG the Brooklyn Bridge deck. Forward/back travels the deck
      // (world +X toward Brooklyn); the camera is pinned to the deck centreline so it can
      // never fly off into open water. Look around freely with the mouse.
      c.x += iy * spd;
      c.z = 150; // deck centreline
      c.y = 52; // eye height above the promenade
      c.x = THREE.MathUtils.clamp(c.x, 410, 1260);
    } else if (cfg.style === "interior") {
      c.addScaledVector(fwd, iy * (cfg.speed * delta));
      // observation decks: flip X-axis so Left moves left / Right moves right (camera-relative)
      c.addScaledVector(right, -ix * (cfg.speed * delta));
      // collision: keep inside the viewing cube, up to the glass
      const half = 23.5;
      c.x = THREE.MathUtils.clamp(c.x, floorPos[0] - half, floorPos[0] + half);
      c.z = THREE.MathUtils.clamp(c.z, floorPos[2] - half, floorPos[2] + half);
      c.y = floorY + 1.6;
    } else {
      // strafe (walk)
      c.addScaledVector(fwd, iy * spd);
      c.addScaledVector(right, ix * spd);
      c.y = cfg.y;
    }

    if (cfg.clamp) {
      c.x = THREE.MathUtils.clamp(c.x, -cfg.clamp[0] - 200, cfg.clamp[0] + 200);
      c.z = THREE.MathUtils.clamp(c.z, -cfg.clamp[1] - 200, cfg.clamp[1] + 200);
    }

    // Guided-transit framing: keep the memorial centred when the pilot is idle.
    const look = TRANSIT_LOOK[mode];
    if (look && !dragging.current && ix === 0 && iy === 0) {
      const dx = look[0] - c.x;
      const dy = look[1] - c.y;
      const dz = look[2] - c.z;
      const horiz = Math.hypot(dx, dz) || 0.001;
      const targetYaw = Math.atan2(dx, dz);
      const targetPitch = Math.atan2(dy, horiz);
      let dYaw = ((targetYaw - yaw.current + Math.PI) % (Math.PI * 2)) - Math.PI;
      const ease = Math.min(1, delta * 1.5);
      yaw.current += dYaw * ease;
      pitch.current += (targetPitch - pitch.current) * ease;
    }

    const p = pitch.current;
    camera.lookAt(
      c.x + Math.sin(yaw.current) * Math.cos(p),
      c.y + Math.sin(p),
      c.z + Math.cos(yaw.current) * Math.cos(p)
    );
  });
  return null;
}

const FOCUS_TARGET = [-40, 55, -80];

function OrbitMode() {
  const controls = useRef();
  const { camera } = useThree();
  const focusAnim = useRef(null);

  useEffect(() => {
    camera.position.set(440, 360, 560);
    camera.fov = 50;
    camera.updateProjectionMatrix();
  }, [camera]);

  useEffect(() => {
    const unsub = useStore.subscribe((s, prev) => {
      if (s.focusRequest && s.focusRequest !== prev.focusRequest && controls.current) {
        focusAnim.current = {
          fromPos: camera.position.clone(),
          fromTarget: controls.current.target.clone(),
          toPos: new THREE.Vector3(...s.focusRequest.position),
          toTarget: new THREE.Vector3(...s.focusRequest.target),
          t: 0,
        };
      }
      if (s.camResetNonce !== prev.camResetNonce && controls.current) {
        camera.fov = 50;
        camera.updateProjectionMatrix();
        focusAnim.current = {
          fromPos: camera.position.clone(),
          fromTarget: controls.current.target.clone(),
          toPos: new THREE.Vector3(440, 360, 560),
          toTarget: new THREE.Vector3(...FOCUS_TARGET),
          t: 0,
        };
      }
    });
    return unsub;
  }, [camera]);

  useFrame((_, delta) => {
    const fa = focusAnim.current;
    if (fa && controls.current) {
      fa.t = Math.min(1, fa.t + delta * 0.9);
      const e = 1 - Math.pow(1 - fa.t, 3);
      camera.position.lerpVectors(fa.fromPos, fa.toPos, e);
      controls.current.target.lerpVectors(fa.fromTarget, fa.toTarget, e);
      controls.current.update();
      if (fa.t >= 1) {
        focusAnim.current = null;
        useStore.getState().clearFocus();
      }
    }
  });

  return (
    <OrbitControls
      ref={controls}
      makeDefault
      enableDamping
      dampingFactor={0.06}
      autoRotate
      autoRotateSpeed={0.26}
      minDistance={40}
      maxDistance={1700}
      maxPolarAngle={Math.PI / 2 - 0.03}
      target={FOCUS_TARGET}
    />
  );
}

export default function Rig() {
  const navMode = useStore((s) => s.navMode);
  const interiorFloor = useStore((s) => s.interiorFloor);
  const vrActive = useStore((s) => s.vrActive);
  if (vrActive) return null;
  if (navMode === "orbit") return <OrbitMode />;
  return <PilotController mode={navMode} floor={interiorFloor} />;
}
